
type CleanedFilter = {
    hospitalId?: string[];
    departmentId?: string[];
    expiringBucket?: number;
    consigned?: number;
    orderBy?: string | undefined;
    queryBuilder?: {
        fieldName: string;
        operator: string;
        value: any;
    }[];
};

type ReportFilter = CleanedFilter;

// Shallow array equality (order-insensitive optional)
const arraysEqual = (a?: any[], b?: any[], { orderMatters = true } = {}): boolean => {
    if (!a && !b) return true;
    if (!a || !b) return false;
    if (a.length !== b.length) return false;
    if (orderMatters) return a.every((val, i) => val === b[i]);

    // order-insensitive compare
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((val, i) => val === sb[i]);
};

// Compare queryBuilder arrays (order-insensitive by normalized key)
const queryBuildersEqual = (
    qb1?: CleanedFilter['queryBuilder'],
    qb2?: CleanedFilter['queryBuilder']
): boolean => {
    if (!qb1 && !qb2) return true;
    if (!qb1 || !qb2) return false;
    if (qb1.length !== qb2.length) return false;

    const normalize = (q: any) => ({
        fieldName: String(q.fieldName ?? ''),
        operator: String(q.operator ?? ''),
        // stringify value safely to avoid object reference differences
        value: q.value === undefined ? null : JSON.stringify(q.value),
    });

    const s1 = qb1.map(normalize).sort((a, b) => a.fieldName.localeCompare(b.fieldName) || a.operator.localeCompare(b.operator));
    const s2 = qb2.map(normalize).sort((a, b) => a.fieldName.localeCompare(b.fieldName) || a.operator.localeCompare(b.operator));

    return s1.every((q, i) => q.fieldName === s2[i].fieldName && q.operator === s2[i].operator && q.value === s2[i].value);
};

// Core equality over the relevant keys
export const filtersEqual = (a?: ReportFilter, b?: ReportFilter): boolean => {
    if (!a && !b) return true;
    if (!a || !b) return false;

    return (
        arraysEqual(a.hospitalId, b.hospitalId) &&
        arraysEqual(a.departmentId, b.departmentId) &&
        (a.expiringBucket ?? null) === (b.expiringBucket ?? null) &&
        (a.consigned ?? null) === (b.consigned ?? null) &&
        (a.orderBy ?? '') === (b.orderBy ?? '') &&
        queryBuildersEqual(a.queryBuilder, b.queryBuilder)
    );
};

export const normalizeFilter = (f: Partial<Record<string, any>> | undefined): CleanedFilter | undefined => {
    if (!f) return undefined;
    return {
        hospitalId: Array.isArray(f.hospitalId) ? f.hospitalId : undefined,
        departmentId: Array.isArray(f.departmentId) ? f.departmentId : undefined,
        expiringBucket: f.expiringBucket ?? undefined, // null -> undefined
        consigned: f.consigned ?? undefined,           // null -> undefined
        orderBy: (f.orderBy ?? undefined) as string | undefined,
        queryBuilder: Array.isArray(f.queryBuilder) ? f.queryBuilder : Array.isArray(f.queryBuilder) ? f.queryBuilder : undefined,
    };
};
